/**
 * More — the phone's way to everything (mobile M3).
 *
 * On a desktop the ⌘K palette is the fast route to every page and action. A
 * phone has no ⌘K, and a palette that floats at the top of the screen is out
 * of thumb reach, so the tab bar's last slot opens this page instead: a search
 * field, then every page, action and card as grouped rows, each a full-width
 * touch target. Same entries as the palette (`PAGES` is shared), so nothing is
 * reachable on one and not the other.
 *
 * The page exists at every width — a link to `/more` on a desktop works — but
 * only the phone's tab bar links to it.
 */

import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  Calculator,
  ChevronRight,
  CreditCard,
  LogOut,
  Moon,
  Plus,
  RefreshCw,
  Search,
  Sun,
  Upload,
} from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useSignOut } from "@/components/AuthGate";
import { PAGES } from "@/components/CommandPalette";
import { Input } from "@/components/ui/primitives";
import { cardsApi, type Card } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { matches } from "@/lib/matches";
import { useCommands } from "@/lib/commands";
import { formatLast4 } from "@/lib/format";
import { useTheme } from "@/lib/useTheme";

type Row = {
  key: string;
  label: string;
  hint?: string;
  icon?: React.ComponentType<{ className?: string }>;
  swatch?: Card["theme"];
  to?: string;
  run?: () => void;
  keywords?: string;
  accent?: boolean;
  danger?: boolean;
};

export function MorePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { openPlanner, syncNow } = useCommands();
  const { theme, toggle } = useTheme();
  const signOut = useSignOut();
  const [query, setQuery] = React.useState("");
  const input = React.useRef<HTMLInputElement>(null);

  // The top bar's search icon lands here with `?search` — open the keyboard.
  React.useEffect(() => {
    if (params.has("search")) input.current?.focus();
  }, [params]);

  const { data: cards } = useQuery({ queryKey: ["cards", false], queryFn: () => cardsApi.list(false) });

  const q = query.trim();
  const groups: { title: string; rows: Row[] }[] = [
    {
      title: "Actions",
      rows: [
        { key: "plan", label: "Which card?", hint: "Plan a purchase", icon: Calculator, run: openPlanner, keywords: "plan purchase", accent: true },
        { key: "sync", label: "Sync inbox now", icon: RefreshCw, run: syncNow, keywords: "email refresh" },
        { key: "upload", label: "Upload a statement", icon: Upload, to: "/statements", keywords: "pdf" },
        { key: "cashback", label: "Add a cashback entry", icon: Plus, to: "/cashback", keywords: "manual" },
        { key: "card", label: "Add a card", icon: CreditCard, to: "/cards", keywords: "new" },
        {
          key: "theme",
          label: `Switch to ${theme === "dark" ? "light" : "dark"} theme`,
          icon: theme === "dark" ? Sun : Moon,
          run: toggle,
          keywords: "mode appearance",
        },
      ],
    },
    {
      title: "Pages",
      rows: PAGES.map((p) => ({ key: p.to, label: p.label, icon: p.icon, to: p.to, keywords: p.keywords.join(" ") })),
    },
    {
      title: "Cards",
      rows: (cards ?? []).map((c) => ({
        key: c.id,
        label: c.nickname || c.product_name || c.issuer,
        hint: formatLast4(c.last4),
        swatch: c.theme,
        to: `/cards/${c.id}`,
        keywords: `${c.issuer} ${c.product_name ?? ""} ${c.last4}`,
      })),
    },
  ];
  if (signOut.enabled) {
    groups.push({
      title: "Account",
      rows: [{ key: "signout", label: "Sign out", icon: LogOut, run: signOut.signOut, danger: true }],
    });
  }

  const shown = groups
    .map((g) => ({ ...g, rows: q ? g.rows.filter((r) => matches(r, q)) : g.rows }))
    .filter((g) => g.rows.length > 0);

  return (
    <div className="space-y-6">
      <h1 className="font-display text-[28px] font-semibold tracking-[-0.03em] text-ink">More</h1>

      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          if (q) navigate(`/transactions?q=${encodeURIComponent(q)}`);
        }}
        className="relative"
      >
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" aria-hidden />
        <Input
          ref={input}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search pages, cards, actions…"
          aria-label="Search"
          enterKeyHint="search"
          className="h-12 rounded-xl pl-10 text-[15px]"
        />
      </form>

      {q && (
        <Group title="Search">
          <RowLink
            row={{ key: "search", label: `Transactions matching “${q}”`, icon: ArrowLeftRight, to: `/transactions?q=${encodeURIComponent(q)}` }}
          />
        </Group>
      )}

      {shown.map((g) => (
        <Group key={g.title} title={g.title}>
          {g.rows.map((r) => (
            <RowLink key={r.key} row={r} />
          ))}
        </Group>
      ))}

      {q && shown.length === 0 && (
        <p className="px-1 text-sm text-muted">No page, card or action matches “{q}”.</p>
      )}
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="label-micro mb-2 px-1">{title}</h2>
      <div className="overflow-hidden rounded-2xl border border-hairline bg-panel">{children}</div>
    </section>
  );
}

function RowLink({ row }: { row: Row }) {
  const Icon = row.icon;
  const body = (
    <>
      {row.swatch !== undefined ? (
        <span
          aria-hidden
          className="h-[18px] w-7 shrink-0 rounded-[4px] bg-raised shadow-[inset_0_1px_0_rgb(255_255_255/0.25)]"
          style={{
            backgroundImage: row.swatch
              ? `linear-gradient(140deg, ${row.swatch.gradient_from}, ${row.swatch.gradient_to})`
              : undefined,
          }}
        />
      ) : Icon ? (
        <span
          className={cn(
            "grid h-8 w-8 shrink-0 place-items-center rounded-lg border",
            row.accent ? "border-brass/25 bg-brass/10 text-brass" : "border-hairline bg-raised text-muted",
            row.danger && "text-overdue",
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
      ) : null}
      <span className={cn("min-w-0 flex-1 truncate text-[15px]", row.danger ? "text-overdue" : "text-ink")}>
        {row.label}
      </span>
      {row.hint && <span className="shrink-0 font-mono text-xs text-faint">{row.hint}</span>}
      {row.to && <ChevronRight className="h-4 w-4 shrink-0 text-faint" aria-hidden />}
    </>
  );
  const cls =
    "flex min-h-[52px] w-full items-center gap-3 border-b border-hairline px-4 text-left last:border-b-0 active:bg-raised";
  return row.to ? (
    <Link to={row.to} className={cls}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={row.run} className={cls}>
      {body}
    </button>
  );
}
